/**
 * AI Router - Main routing logic
 *
 * Analyzes messages and routes them to the appropriate AI provider/model
 *
 * Provider hierarchy:
 * 1. Groq (primary) - Fast & cheap, handles trivial/simple/moderate tasks
 * 2. OpenAI (secondary) - For complex tasks requiring GPT-4
 * 3. Anthropic (expert) - For expert/critical tasks requiring Claude
 */

import type {
  AIProvider,
  AIModel,
  ComplexityLevel,
  MessageAnalysis,
  MessageIntent,
  RoutingDecision,
  AIRoutingResult,
  PipelineStep,
  AIRouterConfig,
  GroqModel,
  OpenAIModel,
  AnthropicModel,
} from './types';
import { DEFAULT_ROUTER_CONFIG } from './types';
import { logRoutingPipeline, logQuickRoute, logRoutingError } from './logger';

// Technical terms for complexity detection
const TECHNICAL_TERMS = [
  'api', 'database', 'sql', 'graphql', 'algorithm', 'function', 'class',
  'typescript', 'javascript', 'react', 'node', 'python', 'rust', 'code',
  'deploy', 'server', 'client', 'backend', 'frontend', 'microservice',
  'docker', 'kubernetes', 'aws', 'cloud', 'authentication', 'encryption',
  'machine learning', 'neural network', 'ai', 'model', 'training',
  'regex', 'parsing', 'compiler', 'runtime', 'memory', 'thread',
  'async', 'promise', 'callback', 'middleware', 'hook', 'state',
];

// Command verbs that indicate tool usage
const COMMAND_VERBS = [
  'create', 'add', 'make', 'build', 'generate', 'write',
  'delete', 'remove', 'cancel', 'archive',
  'update', 'edit', 'modify', 'change',
  'find', 'search', 'list', 'show', 'get',
  'send', 'schedule', 'assign', 'move',
];

// Greeting patterns
const GREETING_PATTERNS = [
  /^(hi|hello|hey|ciao|buongiorno|buonasera|salve)\b/i,
  /^(good\s*(morning|afternoon|evening))/i,
];

/**
 * AI Router class
 */
export class AIRouter {
  private config: AIRouterConfig;
  private providerHealth: Map<AIProvider, boolean> = new Map();

  constructor(config: Partial<AIRouterConfig> = {}) {
    this.config = { ...DEFAULT_ROUTER_CONFIG, ...config };

    // Initialize provider health
    this.providerHealth.set('groq', this.config.providers.groq.enabled);
    this.providerHealth.set('openai', this.config.providers.openai.enabled);
    this.providerHealth.set('anthropic', this.config.providers.anthropic.enabled);
  }

  /**
   * Route a message to the appropriate AI provider/model
   */
  async route(message: string): Promise<AIRoutingResult> {
    const startTime = Date.now();
    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const pipelineSteps: PipelineStep[] = [];
    let stepNumber = 0;

    const addStep = (name: string, description: string, input: any, output: any, decision?: string): void => {
      stepNumber++;
      pipelineSteps.push({
        step: stepNumber,
        name,
        description,
        input,
        output,
        duration: Date.now() - startTime,
        decision,
      });
    };

    try {
      // Step 1: Receive message
      addStep('receive', 'Message received and queued for analysis', { message }, { length: message.length });

      // Step 2: Tokenize and get basic metrics
      const basicMetrics = this.getBasicMetrics(message);
      addStep(
        'tokenize',
        'Extract basic metrics (words, sentences, tokens)',
        { message: message.substring(0, 50) + '...' },
        basicMetrics
      );

      // Step 3: Detect intent
      const intent = this.detectIntent(message, basicMetrics);
      addStep(
        'detect_intent',
        'Analyze message intent and domain',
        { message: message.substring(0, 50) + '...' },
        intent,
        `Category: ${intent.category}, Domain: ${intent.domain}`
      );

      // Step 4: Analyze content
      const contentAnalysis = this.analyzeContent(message);
      addStep(
        'analyze',
        'Deep content analysis (code, technical terms, topics)',
        { message: message.substring(0, 50) + '...' },
        contentAnalysis
      );

      // Step 5: Assess complexity
      const { complexity, complexityScore, complexityFactors } = this.assessComplexity(
        basicMetrics,
        intent,
        contentAnalysis
      );
      addStep(
        'assess_complexity',
        'Calculate message complexity score',
        { metrics: basicMetrics, intent, content: contentAnalysis },
        { complexity, complexityScore, factors: complexityFactors },
        `Complexity: ${complexity.toUpperCase()} (${complexityScore}/100)`
      );

      // Build complete analysis
      const analysis: MessageAnalysis = {
        ...basicMetrics,
        ...contentAnalysis,
        intent,
        complexity,
        complexityScore,
        complexityFactors,
      };

      // Step 6: Check provider availability
      const availableProviders = await this.checkProviderAvailability();
      addStep(
        'check_providers',
        'Verify which AI providers are available',
        { configured: Object.keys(this.config.providers) },
        { available: availableProviders },
        `Available: ${availableProviders.join(', ') || 'None!'}`
      );

      // Step 7: Select provider
      const selectedProvider = this.selectProvider(complexity, availableProviders);
      addStep(
        'select_provider',
        'Choose AI provider based on complexity and preferences',
        { complexity, available: availableProviders, preferences: this.config.preferences },
        { selected: selectedProvider },
        `Selected: ${selectedProvider.toUpperCase()}`
      );

      // Step 8: Select model
      const selectedModel = this.selectModel(selectedProvider, complexity, intent);
      addStep(
        'select_model',
        'Choose specific model for the provider',
        { provider: selectedProvider, complexity, intent },
        { selected: selectedModel },
        `Selected: ${selectedModel}`
      );

      // Step 9: Build decision
      const decision = this.buildDecision(selectedProvider, selectedModel, complexity, analysis);
      addStep(
        'finalize',
        'Build final routing decision with fallbacks',
        { provider: selectedProvider, model: selectedModel },
        decision,
        `Route to ${selectedProvider}/${selectedModel}`
      );

      // Build result
      const result: AIRoutingResult = {
        messageId,
        timestamp: new Date(),
        originalMessage: message,
        analysis,
        decision,
        pipelineSteps,
      };

      // Log the pipeline
      if (this.config.logging.enabled && this.config.logging.logToConsole) {
        if (this.config.logging.verbose) {
          logRoutingPipeline(result);
        } else {
          logQuickRoute(message, selectedProvider, selectedModel, decision.reasoning[0]);
        }
      }

      return result;

    } catch (error) {
      logRoutingError(message, error as Error);
      throw error;
    }
  }

  /**
   * Get basic metrics from message
   */
  private getBasicMetrics(message: string): Pick<MessageAnalysis, 'wordCount' | 'charCount' | 'sentenceCount' | 'questionCount' | 'estimatedTokens'> {
    const words = message.split(/\s+/).filter(w => w.length > 0);
    const sentences = message.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const questions = (message.match(/\?/g) || []).length;

    return {
      wordCount: words.length,
      charCount: message.length,
      sentenceCount: sentences.length,
      questionCount: questions,
      estimatedTokens: Math.ceil(words.length * 1.3), // Rough estimate
    };
  }

  /**
   * Detect message intent
   */
  private detectIntent(message: string, metrics: Pick<MessageAnalysis, 'wordCount' | 'questionCount'>): MessageIntent {
    const lowerMessage = message.toLowerCase();

    // Check for greeting
    if (GREETING_PATTERNS.some(pattern => pattern.test(message))) {
      return {
        category: 'greeting',
        domain: 'general',
        requiresContext: false,
        requiresTools: false,
        estimatedResponseLength: 'short',
      };
    }

    // Check for commands
    const hasCommandVerb = COMMAND_VERBS.some(verb =>
      lowerMessage.includes(verb) || lowerMessage.startsWith(verb)
    );

    // Check for questions
    const isQuestion = metrics.questionCount > 0 ||
      /^(what|who|where|when|why|how|which|can|could|would|is|are|do|does)\b/i.test(message);

    // Detect domain
    let domain: MessageIntent['domain'] = 'general';
    if (TECHNICAL_TERMS.some(term => lowerMessage.includes(term))) {
      domain = 'technical';
    } else if (/\b(fattura|invoice|payment|pagamento|costo|prezzo|budget)\b/i.test(message)) {
      domain = 'business';
    } else if (/\b(progetto|project|task|milestone|deadline|scadenza)\b/i.test(message)) {
      domain = 'business';
    } else if (/\b(write|scrivi|compose|create|genera|generate)\b/i.test(message)) {
      domain = 'creative';
    } else if (/\b(code|codice|function|funzione|class|classe|debug)\b/i.test(message)) {
      domain = 'code';
    } else if (/\b(data|dati|analizza|analyze|report|statistiche)\b/i.test(message)) {
      domain = 'data';
    }

    // Determine category
    let category: MessageIntent['category'];
    if (hasCommandVerb) {
      category = 'command';
    } else if (isQuestion) {
      category = 'question';
    } else if (domain === 'data') {
      category = 'analysis';
    } else if (domain === 'creative' || domain === 'code') {
      category = 'creation';
    } else if (metrics.wordCount < 10) {
      category = 'conversation';
    } else {
      category = 'unknown';
    }

    // Estimate response length
    let estimatedResponseLength: MessageIntent['estimatedResponseLength'];
    if (category === 'greeting' || metrics.wordCount < 5) {
      estimatedResponseLength = 'short';
    } else if (category === 'creation' || domain === 'code' || domain === 'data') {
      estimatedResponseLength = 'long';
    } else {
      estimatedResponseLength = 'medium';
    }

    return {
      category,
      domain,
      requiresContext: category === 'command' || domain === 'business',
      requiresTools: category === 'command',
      estimatedResponseLength,
    };
  }

  /**
   * Analyze message content
   */
  private analyzeContent(message: string): Pick<MessageAnalysis, 'hasCode' | 'hasNumbers' | 'hasTechnicalTerms' | 'hasMultipleTopics'> {
    const lowerMessage = message.toLowerCase();

    // Check for code patterns
    const hasCode = /```|`[^`]+`|function\s*\(|const\s+\w+|let\s+\w+|var\s+\w+|=>|import\s+|export\s+/.test(message);

    // Check for numbers
    const hasNumbers = /\d+/.test(message);

    // Check for technical terms
    const hasTechnicalTerms = TECHNICAL_TERMS.some(term => lowerMessage.includes(term));

    // Check for multiple topics (rough heuristic)
    const topicIndicators = ['also', 'and', 'inoltre', 'e anche', 'poi', 'then', 'next'];
    const hasMultipleTopics = topicIndicators.some(ind => lowerMessage.includes(ind)) ||
      (message.match(/[.!?]/g) || []).length > 2;

    return {
      hasCode,
      hasNumbers,
      hasTechnicalTerms,
      hasMultipleTopics,
    };
  }

  /**
   * Assess message complexity
   */
  private assessComplexity(
    metrics: Pick<MessageAnalysis, 'wordCount' | 'sentenceCount' | 'questionCount' | 'estimatedTokens'>,
    intent: MessageIntent,
    content: Pick<MessageAnalysis, 'hasCode' | 'hasTechnicalTerms' | 'hasMultipleTopics'>
  ): { complexity: ComplexityLevel; complexityScore: number; complexityFactors: string[] } {
    let score = 0;
    const factors: string[] = [];

    // Word count factor (0-20 points)
    if (metrics.wordCount < 5) {
      score += 0;
    } else if (metrics.wordCount < 20) {
      score += 10;
      factors.push('Short message');
    } else if (metrics.wordCount < 50) {
      score += 20;
      factors.push('Medium length');
    } else if (metrics.wordCount < 100) {
      score += 30;
      factors.push('Long message');
    } else {
      score += 40;
      factors.push('Very long message');
    }

    // Technical content (0-20 points)
    if (content.hasCode) {
      score += 20;
      factors.push('Contains code');
    }
    if (content.hasTechnicalTerms) {
      score += 15;
      factors.push('Technical terminology');
    }

    // Intent complexity (0-20 points)
    if (intent.category === 'creation') {
      score += 15;
      factors.push('Creative task');
    }
    if (intent.category === 'analysis') {
      score += 15;
      factors.push('Analysis required');
    }
    if (intent.requiresTools) {
      score += 10;
      factors.push('Requires tool usage');
    }

    // Multiple topics (0-10 points)
    if (content.hasMultipleTopics) {
      score += 10;
      factors.push('Multiple topics');
    }

    // Domain complexity (0-15 points)
    if (intent.domain === 'code') {
      score += 15;
      factors.push('Code domain');
    } else if (intent.domain === 'technical') {
      score += 10;
      factors.push('Technical domain');
    } else if (intent.domain === 'data') {
      score += 10;
      factors.push('Data analysis');
    }

    // Cap at 100
    score = Math.min(score, 100);

    // Determine complexity level
    let complexity: ComplexityLevel;
    if (score < 15) {
      complexity = 'trivial';
    } else if (score < 30) {
      complexity = 'simple';
    } else if (score < 50) {
      complexity = 'moderate';
    } else if (score < 75) {
      complexity = 'complex';
    } else {
      complexity = 'expert';
    }

    return { complexity, complexityScore: score, complexityFactors: factors };
  }

  /**
   * Check provider availability
   */
  private async checkProviderAvailability(): Promise<AIProvider[]> {
    const available: AIProvider[] = [];

    // API keys are managed by the backend gateway.
    // Frontend only checks if the provider is enabled in config.

    if (this.config.providers.groq.enabled) {
      available.push('groq');
    }

    if (this.config.providers.openai.enabled) {
      available.push('openai');
    }

    if (this.config.providers.anthropic.enabled) {
      available.push('anthropic');
    }

    return available;
  }

  /**
   * Select AI provider based on complexity and preferences
   */
  private selectProvider(complexity: ComplexityLevel, available: AIProvider[]): AIProvider {
    const { preferences } = this.config;

    // If only one provider available, use it
    if (available.length === 1) {
      return available[0];
    }

    // If no providers available, default to Groq (will fail gracefully)
    if (available.length === 0) {
      return 'groq';
    }

    // Complexity-based selection
    const complexityOrder: ComplexityLevel[] = ['trivial', 'simple', 'moderate', 'complex', 'expert'];
    const complexityIndex = complexityOrder.indexOf(complexity);
    const maxGroqIndex = complexityOrder.indexOf(preferences.maxComplexityForGroq);

    // Provider selection based on complexity:
    // - trivial/simple/moderate → Groq (fast & cheap)
    // - complex → OpenAI GPT-4
    // - expert → Anthropic Claude

    if (complexityIndex <= maxGroqIndex && available.includes('groq')) {
      // Groq handles trivial to moderate complexity
      return 'groq';
    }

    if (complexity === 'complex') {
      // Complex tasks go to OpenAI
      if (available.includes('openai')) return 'openai';
      if (available.includes('anthropic')) return 'anthropic';
      if (available.includes('groq')) return 'groq'; // Fallback
    }

    if (complexity === 'expert') {
      // Expert tasks go to Anthropic (best reasoning)
      if (available.includes('anthropic')) return 'anthropic';
      if (available.includes('openai')) return 'openai';
      if (available.includes('groq')) return 'groq'; // Fallback
    }

    // Cost-sensitive: prefer Groq (cheapest)
    if (preferences.costSensitive && available.includes('groq')) {
      return 'groq';
    }

    // Quality-first: prefer Anthropic > OpenAI > Groq
    if (preferences.qualityFirst) {
      if (available.includes('anthropic')) return 'anthropic';
      if (available.includes('openai')) return 'openai';
    }

    // Default to first available (Groq should be first)
    return available[0];
  }

  /**
   * Select model for the chosen provider
   */
  private selectModel(provider: AIProvider, complexity: ComplexityLevel, intent: MessageIntent): AIModel {
    const complexityOrder: ComplexityLevel[] = ['trivial', 'simple', 'moderate', 'complex', 'expert'];
    const complexityIndex = complexityOrder.indexOf(complexity);

    switch (provider) {
      case 'groq': {
        const models = this.config.providers.groq.models;
        // Use smaller/faster model for simple tasks, larger for complex
        if (complexityIndex <= 1) {
          // Trivial/Simple: Use fastest model
          if (models.includes('llama-3.1-8b-instant')) return 'llama-3.1-8b-instant';
        }
        if (complexityIndex <= 2) {
          // Moderate: Use better model
          if (models.includes('llama-3.1-70b-versatile')) return 'llama-3.1-70b-versatile';
          if (models.includes('llama-3.1-8b-instant')) return 'llama-3.1-8b-instant';
        }
        // Complex/Expert on Groq (fallback): Use best available
        if (models.includes('llama-3.3-70b-versatile')) return 'llama-3.3-70b-versatile';
        if (models.includes('llama-3.1-70b-versatile')) return 'llama-3.1-70b-versatile';
        return models[0] as GroqModel;
      }

      case 'openai': {
        const models = this.config.providers.openai.models;
        // Simple: GPT-4o-mini (cheap but good)
        if (complexityIndex <= 1) {
          if (models.includes('gpt-4o-mini')) return 'gpt-4o-mini';
          if (models.includes('gpt-3.5-turbo')) return 'gpt-3.5-turbo';
        }
        // Moderate/Complex: GPT-4o
        if (complexityIndex <= 3) {
          if (models.includes('gpt-4o')) return 'gpt-4o';
          if (models.includes('gpt-4-turbo')) return 'gpt-4-turbo';
        }
        // Expert: Best available
        if (models.includes('gpt-4-turbo')) return 'gpt-4-turbo';
        if (models.includes('gpt-4o')) return 'gpt-4o';
        if (models.includes('gpt-4')) return 'gpt-4';
        return models[0] as OpenAIModel;
      }

      case 'anthropic': {
        const models = this.config.providers.anthropic.models;
        // Simple: Haiku (fast & cheap)
        if (complexityIndex <= 1 && models.includes('claude-3-haiku-20240307')) {
          return 'claude-3-haiku-20240307';
        }
        // Moderate/Complex: Sonnet
        if (complexityIndex <= 3 && models.includes('claude-3-5-sonnet-20241022')) {
          return 'claude-3-5-sonnet-20241022';
        }
        // Expert: Opus or best available
        if (models.includes('claude-3-opus-20240229')) return 'claude-3-opus-20240229';
        if (models.includes('claude-3-5-sonnet-20241022')) return 'claude-3-5-sonnet-20241022';
        return models[0] as AnthropicModel;
      }

      default:
        return 'llama-3.1-8b-instant';
    }
  }

  /**
   * Build the final routing decision
   */
  private buildDecision(
    provider: AIProvider,
    model: AIModel,
    complexity: ComplexityLevel,
    analysis: MessageAnalysis
  ): RoutingDecision {
    const reasoning: string[] = [];

    // Add reasoning based on selection
    if (provider === 'groq') {
      reasoning.push('Groq selected for fast & cheap inference');
      if (complexity === 'trivial' || complexity === 'simple') {
        reasoning.push('Simple message handled by Llama on Groq');
      } else if (complexity === 'moderate') {
        reasoning.push('Moderate task uses larger Llama model');
      }
    } else if (provider === 'openai') {
      reasoning.push('OpenAI selected for complex reasoning');
      if (complexity === 'complex') {
        reasoning.push('Complex task benefits from GPT-4');
      }
    } else if (provider === 'anthropic') {
      reasoning.push('Anthropic selected for expert-level task');
      if (complexity === 'expert') {
        reasoning.push('Expert task requires Claude advanced reasoning');
      }
    }

    if (analysis.intent.requiresTools) {
      reasoning.push('Message requires tool usage for data operations');
    }

    if (analysis.hasCode) {
      reasoning.push('Code content detected, using capable model');
    }

    // Determine fallback
    let fallbackProvider: AIProvider | undefined;
    let fallbackModel: AIModel | undefined;

    if (provider === 'groq') {
      // Groq fallback to OpenAI
      if (this.config.providers.openai.enabled) {
        fallbackProvider = 'openai';
        fallbackModel = 'gpt-4o-mini';
      }
    } else if (provider === 'openai') {
      // OpenAI fallback to Groq
      fallbackProvider = 'groq';
      fallbackModel = 'llama-3.1-70b-versatile';
    } else if (provider === 'anthropic') {
      // Anthropic fallback to OpenAI
      if (this.config.providers.openai.enabled) {
        fallbackProvider = 'openai';
        fallbackModel = 'gpt-4o';
      } else {
        fallbackProvider = 'groq';
        fallbackModel = 'llama-3.1-70b-versatile';
      }
    }

    // Estimate cost
    let estimatedCost: RoutingDecision['estimatedCost'];
    if (provider === 'groq') {
      estimatedCost = 'low'; // Groq is very cheap
    } else if (model.includes('mini') || model.includes('haiku') || model.includes('3.5')) {
      estimatedCost = 'low';
    } else if (model.includes('4o') || model.includes('sonnet')) {
      estimatedCost = 'medium';
    } else {
      estimatedCost = 'high';
    }

    // Estimate latency
    let estimatedLatency: RoutingDecision['estimatedLatency'];
    if (provider === 'groq') {
      estimatedLatency = 'fast'; // Groq is extremely fast
    } else if (model.includes('mini') || model.includes('haiku')) {
      estimatedLatency = 'fast';
    } else {
      estimatedLatency = 'medium';
    }

    return {
      provider,
      model,
      fallbackProvider,
      fallbackModel,
      reasoning,
      confidence: 0.85,
      estimatedCost,
      estimatedLatency,
    };
  }

  /**
   * Update provider health status
   */
  setProviderHealth(provider: AIProvider, healthy: boolean): void {
    this.providerHealth.set(provider, healthy);
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<AIRouterConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current configuration
   */
  getConfig(): AIRouterConfig {
    return { ...this.config };
  }
}

// Singleton instance
let routerInstance: AIRouter | null = null;

/**
 * Get or create the AI Router instance
 */
export function getAIRouter(config?: Partial<AIRouterConfig>): AIRouter {
  if (!routerInstance) {
    routerInstance = new AIRouter(config);
  } else if (config) {
    routerInstance.updateConfig(config);
  }
  return routerInstance;
}

/**
 * Quick route function for simple usage
 */
export async function routeMessage(message: string): Promise<AIRoutingResult> {
  const router = getAIRouter();
  return router.route(message);
}
